/* eslint-disable react/no-unescaped-entities */
import React from 'react';
import PropTypes from 'prop-types';
import { Link, graphql } from 'gatsby';
import styled from 'styled-components';
import breakpoint from 'styled-components-breakpoint';

import { getLanguageCode, useLocalizedNews } from '../utils';

import Grid from './Grid';
import Cell from './Cell';
import Block from './Block';
import H2 from './H2';
import H4 from './H4';
import H5 from './H5';
import P from './P';
import Image from './Image';

const NewsCell = styled(Cell)`
  display: flex;
  maxwidth: unset;
  overflow: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;

  ${breakpoint('tablet')`
    display: block;
    overflow: hidden;
  `};
`;

const NewsItem = styled(Grid)`
  flex: 1 0 80%;
  scroll-snap-align: start;
`;

const PageCaseAndNews = ({ content, selectedLocale }) => {
  const numNewsItems = 2;
  const { news } = useLocalizedNews(selectedLocale, numNewsItems);

  return (
    <Block
      id={content.internal_link_id}
      py={{ mobile: 'baseUp4', tablet: 'baseUp12' }}
    >
      <H2
        textAlign="center"
        mb={{ mobile: 'baseUp3', tablet: 'baseUp10' }}
        color="coral"
      >
        {content.title1.text}
      </H2>

      <Grid>
        <Cell size={{ mobile: 1, tablet: 1 / 2 }}>
          {content.case_link.document ? (
            <Block px="baseUnit">
              <Image content={content.case_link.document.data.list_image} />
              <Block mt="baseUp2" mb={{ mobile: 'baseUp2', tablet: '0' }}>
                <H4 color="coral" mb="baseUnit">
                  {content.case_link.document.data.list_title.text}
                </H4>
                <P>{content.case_link.document.data.list_description.text}</P>
                <P mb="0">
                  <Link
                    to={`${
                      selectedLocale !== 'en-us'
                        ? `/${getLanguageCode(selectedLocale)}`
                        : ''
                    }/cases/${content.case_link.document.uid}`}
                  >
                    {content.case_link.document.data.list_read_more_text.text}{' '}
                    &gt;
                  </Link>
                </P>
              </Block>
            </Block>
          ) : null}
        </Cell>
        <NewsCell
          size={{ mobile: 1, tablet: 1 / 2 }}
          // p={{ tablet: 'baseUnit' }}
        >
          {news.map(({ node }, i) => (
            <NewsItem
              key={`news-${node.uid}`}
              mb={{ tablet: i === news.length - 1 ? '0' : 'baseUp4' }}
              px="baseUnit"
            >
              <Cell size={{ mobile: 1, tablet: 1 / 2 }}>
                <Image content={node.data.image} />
              </Cell>
              <Cell
                size={{ mobile: 1, tablet: 1 / 2 }}
                pl={{ tablet: 'baseUnit' }}
                pt={{ mobile: 'baseUnit', tablet: '0' }}
              >
                <H5 color="coral" mb="0">
                  {node.data.title.text}
                </H5>
                <P fontSize="baseDown1" mb="baseDown1">
                  {node.data.date}
                </P>
                <P mb="baseDown2">{node.data.short_description.text}</P>

                <P mb="0">
                  <Link
                    to={`${
                      selectedLocale !== 'en-us'
                        ? `/${getLanguageCode(selectedLocale)}`
                        : ''
                    }/news/${node.uid}`}
                  >
                    {node.data.read_more_text} &gt;
                  </Link>
                </P>
              </Cell>
            </NewsItem>
          ))}
        </NewsCell>
      </Grid>
    </Block>
  );
};

PageCaseAndNews.propTypes = {
  content: PropTypes.shape({
    internal_link_id: PropTypes.string,
    title1: PropTypes.shape({ text: PropTypes.string }),
    case_link: PropTypes.shape({
      document: PropTypes.shape({
        uid: PropTypes.string,
        data: PropTypes.shape({
          list_title: PropTypes.shape({ text: PropTypes.string }),
          list_image: PropTypes.shape({
            localFile: PropTypes.shape({
              childImageSharp: PropTypes.shape({ fluid: PropTypes.shape({}) }),
            }),
            alt: PropTypes.string,
          }),
          list_description: PropTypes.shape({ text: PropTypes.string }),
          list_read_more_text: PropTypes.shape({ text: PropTypes.string }),
        }),
      }),
    }),
  }).isRequired,
  selectedLocale: PropTypes.string.isRequired,
};

export default PageCaseAndNews;

export const query = graphql`
  fragment PageCaseAndNewsFragment on PrismicPageBodyCaseNews {
    id
    slice_type
    primary {
      internal_link_id
      title1 {
        text
      }
      case_link {
        document {
          ... on PrismicPage {
            uid
            data {
              list_image {
                alt
                localFile {
                  childImageSharp {
                    fluid(maxWidth: 600) {
                      ...GatsbyImageSharpFluid
                    }
                  }
                }
              }
              list_title {
                text
              }
              list_description {
                text
              }
              list_read_more_text {
                text
              }
            }
          }
        }
      }
    }
  }
`;
